import React, { createContext, useContext, useState, useEffect } from "react";
import { post } from "../services/api";
import { toast } from "react-toastify";
import "./ChatModal.css";

const ChatModalContext = createContext();

export const ChatModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);

  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    setIsOpen(false);
    setMessages([]);
  };

  return (
    <ChatModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {isOpen && (
        <ChatModal
          messages={messages}
          setMessages={setMessages}
          onClose={closeModal}
        />
      )}
    </ChatModalContext.Provider>
  );
};

export const useChatModal = () => useContext(ChatModalContext);

const ChatModal = ({ messages, setMessages, onClose }) => {
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const messagesEndRef = React.useRef(null);

  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessageLines = input
      .split("\n")
      .filter((line) => line.trim() !== "");
    const userMessage = { text: userMessageLines, sender: "user" };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await post(
        "Chatbot/ask",
        {
          sender: roleId == 9 || roleId == 10 ? "parent" : "student",
          message: input,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response) {
        const messageLines = response.reply
          .split("\n")
          .filter((line) => line.trim() !== "");
        setMessages((prev) => [...prev, { text: messageLines, sender: "ai" }]);
        // toast.success(response.message || 'Message sent successfully!');
      } else {
        setMessages((prev) => [
          ...prev,
          { text: ["Bot is busy at the moment."], sender: "ai" },
        ]);
      }
    } catch (error) {
      console.error("Error fetching AI response:", error);
      // toast.error('An error occurred while sending the message.');
      setMessages((prev) => [
        ...prev,
        { text: ["Bot is busy at the moment."], sender: "ai" },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMinimize = () => setIsMinimized(!isMinimized);

  return (
    <>
      {isMinimized ? (
        <div className="chat-minimized" onClick={toggleMinimize} title="Ask AI">
          <i className="fa fa-robot fs-7"></i>
        </div>
      ) : (
        <div className="chat-container">
          <div className="chat-header bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
            <h5 className="mb-0 font-semibold text-white">
              <i className="fa fa-robot me-2"></i>Ask AI
            </h5>
            <div className="chat-controls">
              <button
                type="button"
                className="btn btn-sm text-white"
                onClick={toggleMinimize}
                title="Minimize"
              >
                <i className="fa fa-minus"></i>
              </button>
              <button
                type="button"
                className="btn btn-sm text-white"
                onClick={onClose}
                title="Close"
              >
                <i className="fa fa-times"></i>
              </button>
            </div>
          </div>
          <div
            className="chat-modal-body"
            style={{ minHeight: "300px", overflowY: "auto" }}
          >
            {messages.length === 0 ? (
              <div className="text-center">
                <i className="fa fa-robot fs-5 text-black-50 mt-2"></i>
                <h4 className="mt-2 text-black-50">Start a Conversation</h4>
                <p className="mt-2 fs-9 mb-0 text-black-50">
                  Welcome! 👋 I'm here to help you.
                </p>
                <p className="mt-1 fs-9 mb-0 text-black-50">
                  Do you need assistance with anything today?
                </p>
                <p className="mt-1 fs-9 mb-0 text-black-50">
                  Type a message to begin chatting
                  <br /> with the Ask AI.
                </p>
              </div>
            ) : (
              <div className="chat-messages">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`chat-message-wrapper ${
                      msg.sender === "user"
                        ? "user-message-wrapper"
                        : "ai-message-wrapper"
                    }`}
                  >
                    {msg.sender === "ai" && (
                      <div className="chat-icon">
                        <i className="fa fa-robot fs-8"></i>
                      </div>
                    )}
                    <div
                      className={`chat-message ${
                        msg.sender === "user" ? "user-message" : "ai-message"
                      } animate-slide-in`}
                    >
                      {Array.isArray(msg.text) ? (
                        msg.text.map((line, lineIndex) => (
                          <span key={lineIndex}>
                            {line}
                            {lineIndex < msg.text.length - 1 && (
                              <>
                                <br />
                                <br />
                              </>
                            )}
                          </span>
                        ))
                      ) : (
                        <span>{msg.text}</span>
                      )}
                    </div>
                    {msg.sender === "user" && (
                      <div className="chat-icon">
                        <i className="fa fa-circle-user fs-8"></i>
                      </div>
                    )}
                  </div>
                ))}
                {isLoading && (
                  <div className="chat-message-wrapper ai-message-wrapper">
                    <div className="chat-icon">
                      <i className="fa fa-robot fs-8"></i>
                    </div>
                    <div className="chat-message ai-message animate-pulse">
                      Typing...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
          <div className="chat-footer">
            <form onSubmit={handleSendMessage} className="chat-input-form">
              <input
                type="text"
                className="form-control chat-input shadow-sm"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                disabled={isLoading}
              />
              <button
                type="submit"
                className="btn ps-2 pe-1 text-info py-0"
                disabled={isLoading}
              >
                <i className="fa fa-paper-plane fs-7"></i>
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatModal;
